import React, { useEffect, useRef, useState } from 'react';
import { UploadCloud, X, AlertCircle } from 'lucide-react';

const MIMES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/**
 * UploadImagenes — drag&drop + click para adjuntar hasta `max` imágenes.
 * Controlado: value = File[], onChange(nuevaArray). Valida MIME/tamaño/cupo
 * en cliente (defensa en profundidad; el backend revalida).
 */
export default function UploadImagenes({ value = [], onChange, max = 5, maxBytes = 5 * 1024 * 1024, disabled = false }) {
  const inputRef = useRef(null);
  const urlsRef = useRef(new Map()); // File -> objectURL
  const [drag, setDrag] = useState(false);
  const [errores, setErrores] = useState([]);

  // Revoca todos los ObjectURL al desmontar.
  useEffect(() => () => {
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current.clear();
  }, []);

  function urlDe(file) {
    if (!urlsRef.current.has(file)) urlsRef.current.set(file, URL.createObjectURL(file));
    return urlsRef.current.get(file);
  }

  function procesar(fileList) {
    if (disabled) return;
    const entrantes = Array.from(fileList || []);
    const errs = [];
    const aceptados = [];
    for (const f of entrantes) {
      if (!MIMES.includes(f.type)) { errs.push(`${f.name} no es una imagen — se ignorará.`); continue; }
      if (f.size > maxBytes) { errs.push(`${f.name} supera 5MB — se ignorará.`); continue; }
      aceptados.push(f);
    }
    let nuevos = [...value, ...aceptados];
    if (nuevos.length > max) {
      errs.push(`Máximo ${max} archivos — se ignorarán los demás.`);
      nuevos = nuevos.slice(0, max);
    }
    setErrores(errs);
    onChange(nuevos);
  }

  function quitar(idx) {
    const f = value[idx];
    if (f && urlsRef.current.has(f)) {
      URL.revokeObjectURL(urlsRef.current.get(f));
      urlsRef.current.delete(f);
    }
    onChange(value.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
        onDrop={(e) => { e.preventDefault(); setDrag(false); procesar(e.dataTransfer.files); }}
        className={
          'border-2 border-dashed rounded-lg p-6 text-center transition-colors ' +
          (disabled ? 'opacity-50 cursor-not-allowed ' : 'cursor-pointer ') +
          (drag ? 'border-primary bg-primary/5' : 'border-slate-300 hover:border-slate-400')
        }
      >
        <UploadCloud className="w-6 h-6 mx-auto text-slate-400 mb-2" />
        <div className="text-sm text-slate-600">Arrastra imágenes aquí o haz clic para seleccionar</div>
        <div className="text-[11px] text-slate-400 mt-1">PNG, JPG, GIF, WebP · máx. 5 MB · hasta {max} imágenes</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="hidden"
          disabled={disabled}
          onChange={(e) => { procesar(e.target.files); e.target.value = ''; }}
        />
      </div>

      {errores.length > 0 && (
        <ul className="mt-2 space-y-1">
          {errores.map((e, i) => (
            <li key={i} className="text-xs text-amber-700 flex items-start gap-1">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{e}
            </li>
          ))}
        </ul>
      )}

      {value.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {value.map((f, i) => (
            <div key={i} className="relative w-24">
              <div className="w-24 h-24 rounded-md overflow-hidden border border-slate-200 bg-slate-50">
                <img src={urlDe(f)} alt={f.name} className="w-full h-full object-cover" />
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => quitar(i)}
                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-slate-800 text-white grid place-items-center hover:bg-red-600"
                  title="Quitar"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              <div className="text-[10px] text-slate-500 mt-1 truncate" title={f.name}>{f.name}</div>
              <div className="text-[10px] text-slate-400">{(f.size / 1024).toFixed(0)} KB</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
